import { useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { Category, PostSummary } from '@/lib/posts'
import Reveal from '@/components/preview/Reveal'

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function EditorialPostList({
  posts,
  categories = [],
  tone = 'dark',
}: {
  posts: Array<PostSummary>
  categories?: Array<Category>
  tone?: 'light' | 'dark'
}) {
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set())
  const dark = tone === 'dark'

  return (
    <div
      className={dark ? 'border-b border-white/15' : 'border-b border-ink/15'}
    >
      {posts.map((post, index) => {
        const key = `${post.category}/${post.slug}`
        const category = categories.find((item) => item.slug === post.category)
        const expanded = expandedPosts.has(key)
        const tags = expanded
          ? post.frontmatter.tags
          : post.frontmatter.tags.slice(0, 3)

        return (
          <Reveal key={key} delay={Math.min(index, 6) * 60}>
            <article
              className={`group grid grid-cols-[2.75rem_minmax(0,1fr)_auto] gap-4 border-t py-7 transition-colors sm:grid-cols-[4rem_minmax(0,1fr)_auto] sm:gap-6 sm:py-9 ${dark ? 'border-white/15 hover:bg-white/[0.04]' : 'border-ink/15 hover:bg-ink/[0.035]'}`}
            >
              <span
                className={`preview-mono pt-1 text-xs font-bold sm:text-sm ${dark ? 'text-sage-200' : 'text-forest'}`}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0">
                <Link
                  to="/blog/$category/$slug"
                  params={{ category: post.category, slug: post.slug }}
                  className="focus-ring block"
                >
                  <h2
                    className={`font-heading text-3xl leading-[1.02] tracking-[-0.025em] transition-colors sm:text-5xl ${dark ? 'text-cream-50 group-hover:text-sage-200' : 'text-ink group-hover:text-forest'}`}
                  >
                    {post.frontmatter.title}
                  </h2>
                  <p
                    className={`mt-3 max-w-3xl text-sm leading-6 sm:text-base sm:leading-7 ${dark ? 'text-cream-50/85' : 'text-ink-soft'}`}
                  >
                    {post.frontmatter.description}
                  </p>
                </Link>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span
                    className={`preview-mono text-[9px] font-bold uppercase tracking-[0.18em] sm:text-[10px] ${dark ? 'text-cream-50/65' : 'text-muted'}`}
                  >
                    {category?.name ?? post.category} —{' '}
                    {formatDate(post.frontmatter.date)}
                    {post.frontmatter.series
                      ? ` — ${post.frontmatter.series}`
                      : ''}
                  </span>
                  {tags.map((tag) => (
                    <Link
                      key={tag}
                      to="/blog/tag/$tag"
                      params={{ tag }}
                      className={`preview-mono focus-ring text-[9px] font-bold uppercase tracking-[0.14em] transition-colors ${dark ? 'text-sage-200 hover:text-sage-200' : 'text-forest hover:text-forest'}`}
                    >
                      #{tag}
                    </Link>
                  ))}
                  {post.frontmatter.tags.length > 3 && (
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() =>
                        setExpandedPosts((current) => {
                          const next = new Set(current)
                          if (next.has(key)) next.delete(key)
                          else next.add(key)
                          return next
                        })
                      }
                      className={`preview-mono focus-ring text-[9px] font-bold uppercase tracking-[0.14em] ${dark ? 'text-cream-50/65 hover:text-sage-200' : 'text-muted hover:text-forest'}`}
                    >
                      {expanded
                        ? 'Less'
                        : `+${post.frontmatter.tags.length - 3} tags`}
                    </button>
                  )}
                </div>
              </div>
              <Link
                to="/blog/$category/$slug"
                params={{ category: post.category, slug: post.slug }}
                aria-label={`Read ${post.frontmatter.title}`}
                className="focus-ring self-start"
              >
                <ArrowUpRight
                  className={`h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${dark ? 'text-sage-200' : 'text-forest'}`}
                />
              </Link>
            </article>
          </Reveal>
        )
      })}
    </div>
  )
}
